import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';

const APP_MODULES = [
  'header', 'sidebar', 'dashboard', 'clients', 'transactions',
  'investigations', 'rfi', 'calculator', 'sessions', 'user_actions',
  'settings', 'roles', 'business_info', 'new_va_account',
  'add_transactions', 'notifications'
];

const INITIAL_MODULES = new Set(['shared', 'auth', 'header']);

@Injectable({ providedIn: 'root' })
export class TranslationExtenderService {
  private readonly http = inject(HttpClient);
  private readonly translate = inject(TranslateService);

  private readonly loadedByLang = new Map<string, Set<string>>();
  private readonly inFlight = new Map<string, Observable<void>>();

  private getLoaded(lang: string): Set<string> {
    if (!this.loadedByLang.has(lang)) {
      this.loadedByLang.set(lang, new Set(INITIAL_MODULES));
    }
    return this.loadedByLang.get(lang)!;
  }

  load(lang: string = 'en'): Observable<void> {
    const loaded = this.getLoaded(lang);
    const pending = APP_MODULES.filter(m => !loaded.has(m));

    if (pending.length === 0) return of(void 0);

    // Return in-flight observable if already loading for this language
    if (this.inFlight.has(lang)) return this.inFlight.get(lang)!;

    const requests = pending.map(module =>
      this.http.get<any>(`./assets/i18n/${lang}/${module}.json`).pipe(
        catchError(() => of({}))
      )
    );

    const obs$ = forkJoin(requests).pipe(
      map(responses => {
        const merged: any = {};
        pending.forEach((module, i) => {
          merged[module] = responses[i];
          loaded.add(module);
        });
        this.translate.setTranslation(lang, merged, true);
        this.inFlight.delete(lang);
      }),
      shareReplay(1)
    );

    this.inFlight.set(lang, obs$);
    return obs$;
  }
}
